/**
 * lib/auth/handle-unrecoverable-auth.ts
 *
 * What it does:
 *   Drives the terminal UX when the Clerk auth-recovery interceptor in
 *   lib/utils/index.js has exhausted its retry budget: shows one toast and
 *   hard-redirects to /dashboard. Dedupes so a burst of concurrent 401s
 *   yields a single toast + single navigation. The `reason` picks the toast
 *   copy: "expired" for a confirmed signout, "verification-timeout" when
 *   token refreshes kept timing out (session may still be valid).
 *
 * What it does NOT do:
 *   Has no opinion on WHEN auth is unrecoverable — the interceptor decides
 *   and calls this only from its failure path. Fires no telemetry (that is
 *   recovery-telemetry.ts). No-ops during SSR.
 *
 * Depends on:
 *   - @/components/toasts (reboltToast)
 */

import { reboltToast } from "@/components/toasts";

export type UnrecoverableAuthReason = "expired" | "verification-timeout";

const TERMINAL_AUTH_MESSAGES: Record<UnrecoverableAuthReason, string> = {
  expired: "Your session expired. Please sign in again.",
  "verification-timeout": "Having trouble verifying your session. Redirecting you to sign in.",
};

// Module-level latch: once we've started the terminal flow we're navigating
// away, so further calls in the same burst must stay silent.
let handled = false;

// Brief pause so the toast paints before the hard navigation tears down the page.
const REDIRECT_DELAY_MS = 1200;

export function handleUnrecoverableAuth(options: { reason?: UnrecoverableAuthReason } = {}): void {
  if (typeof window === "undefined" || handled) return;
  handled = true;

  reboltToast.error(TERMINAL_AUTH_MESSAGES[options.reason ?? "expired"], {
    opts: { id: "auth-session-expired", duration: REDIRECT_DELAY_MS + 2000 },
  });

  // Hard navigation (not router.replace) clears the stale SWR cache and
  // re-runs middleware.ts, which routes to sign-in if the session is gone.
  setTimeout(() => {
    window.location.assign("/dashboard");
  }, REDIRECT_DELAY_MS);
}
